import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, ChevronDown, ChevronUp, HelpCircle, ShoppingBag, Store, CreditCard, Truck, User, Shield, MessageCircle, FileText } from 'lucide-react'
import { Link } from 'react-router-dom'

interface FAQ {
  id: string
  category: string
  question: string
  answer: string
}

const HelpCenter = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)

  const categories = [
    { id: 'all', name: 'Todas las Categorías', icon: HelpCircle },
    { id: 'getting-started', name: 'Primeros Pasos', icon: User },
    { id: 'shopping', name: 'Compras y Pedidos', icon: ShoppingBag },
    { id: 'stores', name: 'Negocios', icon: Store },
    { id: 'payments', name: 'Pagos', icon: CreditCard },
    { id: 'delivery', name: 'Entrega', icon: Truck },
    { id: 'account', name: 'Mi Cuenta', icon: User },
    { id: 'security', name: 'Seguridad', icon: Shield },
    { id: 'technical', name: 'Soporte Técnico', icon: MessageCircle }
  ]

  const faqs: FAQ[] = [
    // Primeros Pasos
    {
      id: '1',
      category: 'getting-started',
      question: '¿Cómo me registro en ColmaFácil?',
      answer: 'Para registrarte, haz clic en el botón "Registrarse" en la pantalla de inicio. Puedes registrarte con tu email y contraseña, o usar tu cuenta de Google o Facebook. Completa el formulario con tu información personal y verifica tu email para activar tu cuenta.'
    },
    {
      id: '2',
      category: 'getting-started',
      question: '¿Cómo encuentro colmados cerca de mí?',
      answer: 'ColmaFácil detecta automáticamente tu ubicación para mostrarte los colmados más cercanos. También puedes buscar por nombre, categoría o usar los filtros disponibles. Los colmados se ordenan por cercanía y calificación.'
    },
    {
      id: '3',
      category: 'getting-started',
      question: '¿Es gratis usar ColmaFácil?',
      answer: 'Sí, registrarse y usar ColmaFácil como cliente es completamente gratis. Solo pagas por los productos que compres y el costo de delivery (si aplica) establecido por cada colmado.'
    },
    // Compras y Pedidos
    {
      id: '4',
      category: 'shopping',
      question: '¿Cómo realizo un pedido?',
      answer: '1. Selecciona un colmado de tu preferencia. 2. Navega por su catálogo de productos. 3. Agrega productos a tu carrito. 4. Revisa tu carrito y selecciona dirección de entrega. 5. Elige método de pago. 6. Confirma tu pedido. El colmado recibirá tu pedido y te notificará cuando sea aceptado.'
    },
    {
      id: '5',
      category: 'shopping',
      question: '¿Puedo mezclar productos de diferentes colmados en un mismo pedido?',
      answer: 'No, cada pedido debe ser de un solo colmado. Esto permite que los colmados gestionen mejor sus pedidos y entregas. Si deseas productos de diferentes colmados, deberás realizar pedidos separados.'
    },
    {
      id: '6',
      category: 'shopping',
      question: '¿Cómo cancelo un pedido?',
      answer: 'Puedes cancelar un pedido antes de que sea aceptado por el colmado. Una vez aceptado, la cancelación está sujeta a la política del colmado. Ve a "Mis Pedidos", selecciona el pedido y usa la opción de cancelar si está disponible.'
    },
    {
      id: '7',
      category: 'shopping',
      question: '¿Qué hago si un producto no está disponible?',
      answer: 'Si un producto no está disponible, el colmado puede cancelar ese artículo de tu pedido o contactarte para ofrecerte una alternativa. Recibirás una notificación sobre cualquier cambio en tu pedido.'
    },
    // Negocios
    {
      id: '8',
      category: 'stores',
      question: '¿Cómo registro mi colmado en ColmaFácil?',
      answer: 'Para registrar tu colmado, primero debes crear una cuenta como cliente. Luego, ve a "Únete como Negocio" o "Solicitar ser Dueño de Negocio" y completa el formulario con la información de tu colmado. Un administrador revisará tu solicitud y te notificará cuando sea aprobada.'
    },
    {
      id: '9',
      category: 'stores',
      question: '¿Cuánto cuesta tener mi negocio en ColmaFácil?',
      answer: 'El costo varía según el plan que elijas. Contacta a nuestro equipo de ventas para conocer los planes disponibles y encontrar el que mejor se adapte a tu negocio. Puedes escribirnos a colmafacil@gmail.com o llamarnos al +1 (829) 441-9998.'
    },
    {
      id: '10',
      category: 'stores',
      question: '¿Cómo gestiono los pedidos de mi colmado?',
      answer: 'Una vez que tu colmado esté aprobado, tendrás acceso al panel de administración donde podrás ver todos los pedidos, cambiar su estado (recibido, en preparación, enviado, entregado), gestionar tu inventario y más.'
    },
    // Pagos
    {
      id: '11',
      category: 'payments',
      question: '¿Qué métodos de pago aceptan?',
      answer: 'Los métodos de pago aceptados varían según cada colmado. Generalmente incluyen efectivo, tarjetas de crédito/débito, y transferencias. El método de pago disponible se muestra durante el proceso de checkout.'
    },
    {
      id: '12',
      category: 'payments',
      question: '¿Es seguro pagar en ColmaFácil?',
      answer: 'Sí, ColmaFácil utiliza medidas de seguridad avanzadas para proteger tu información de pago. Todos los datos se transmiten de forma cifrada. Sin embargo, los pagos son procesados directamente por cada colmado, no por ColmaFácil.'
    },
    {
      id: '13',
      category: 'payments',
      question: '¿Puedo pagar con efectivo?',
      answer: 'Sí, muchos colmados aceptan pago en efectivo al momento de la entrega. Esta opción estará disponible durante el checkout si el colmado la ofrece.'
    },
    // Entrega
    {
      id: '14',
      category: 'delivery',
      question: '¿Cuánto tiempo tarda la entrega?',
      answer: 'El tiempo de entrega varía según el colmado y tu ubicación. Generalmente, las entregas se realizan entre 30 minutos y 2 horas. El tiempo estimado se muestra en el perfil de cada colmado y durante el checkout.'
    },
    {
      id: '15',
      category: 'delivery',
      question: '¿Cuánto cuesta el delivery?',
      answer: 'El costo de delivery es establecido por cada colmado y puede variar según la distancia. El costo se muestra claramente antes de confirmar tu pedido. Algunos colmados ofrecen delivery gratis con pedidos mínimos.'
    },
    {
      id: '16',
      category: 'delivery',
      question: '¿Puedo hacer seguimiento de mi pedido?',
      answer: 'Sí, puedes hacer seguimiento de tu pedido en tiempo real desde la sección "Mis Pedidos". Verás el estado actual de tu pedido y recibirás notificaciones cuando cambie de estado.'
    },
    // Mi Cuenta
    {
      id: '17',
      category: 'account',
      question: '¿Cómo cambio mi información personal?',
      answer: 'Ve a tu perfil (icono de usuario) y selecciona "Editar Perfil". Ahí podrás actualizar tu nombre, email, teléfono, foto de perfil y otras informaciones personales.'
    },
    {
      id: '18',
      category: 'account',
      question: '¿Cómo cambio mi contraseña?',
      answer: 'En tu perfil, busca la opción "Cambiar Contraseña". Ingresa tu contraseña actual y la nueva contraseña. Asegúrate de usar una contraseña segura con al menos 8 caracteres.'
    },
    {
      id: '19',
      category: 'account',
      question: '¿Cómo gestiono mis direcciones de entrega?',
      answer: 'En tu perfil, ve a "Direcciones" o "Mis Direcciones". Puedes agregar, editar o eliminar direcciones. También puedes marcar una dirección como predeterminada para agilizar futuros pedidos.'
    },
    // Seguridad
    {
      id: '20',
      category: 'security',
      question: '¿Cómo protegen mis datos personales?',
      answer: 'ColmaFácil cumple con la Ley 172-13 de Protección de Datos Personales de República Dominicana. Utilizamos cifrado SSL/TLS, almacenamiento seguro y medidas de seguridad avanzadas. Puedes leer más en nuestra Política de Privacidad.'
    },
    {
      id: '21',
      category: 'security',
      question: '¿Qué hago si creo que mi cuenta fue comprometida?',
      answer: 'Si sospechas que tu cuenta fue comprometida, cambia tu contraseña inmediatamente y contáctanos a colmafacil@gmail.com o +1 (829) 441-9998. Revisaremos tu cuenta y tomaremos las medidas necesarias.'
    },
    // Soporte Técnico
    {
      id: '22',
      category: 'technical',
      question: 'La app no carga correctamente, ¿qué hago?',
      answer: 'Intenta los siguientes pasos: 1. Cierra completamente la app y ábrela nuevamente. 2. Verifica tu conexión a internet. 3. Limpia la caché de tu navegador o reinstala la app. 4. Si el problema persiste, contáctanos.'
    },
    {
      id: '23',
      category: 'technical',
      question: 'No recibo notificaciones, ¿cómo las activo?',
      answer: 'Verifica la configuración de notificaciones en tu dispositivo y en la configuración de la app. Asegúrate de que las notificaciones estén habilitadas para ColmaFácil en la configuración de tu sistema operativo.'
    },
    {
      id: '24',
      category: 'technical',
      question: '¿Cómo reporto un error o problema técnico?',
      answer: 'Puedes reportar problemas técnicos a través de nuestro formulario de contacto en la sección "Contáctanos", seleccionando "Soporte Técnico" como tipo de consulta. Incluye detalles del problema y capturas de pantalla si es posible.'
    }
  ]

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === null || 
      selectedCategory === 'all' || 
      faq.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 lg:relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base font-medium">Volver</span>
          </button>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
            Centro de Ayuda
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            Encuentra respuestas a tus preguntas o contáctanos para más ayuda
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar preguntas frecuentes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
            Categorías
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {categories.map((category) => {
              const Icon = category.icon
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id === 'all' ? null : category.id)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    (selectedCategory === null && category.id === 'all') ||
                    selectedCategory === category.id
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-primary-50'
                  }`}
                >
                  <Icon className="w-6 h-6 mb-2" />
                  <p className="text-sm font-medium">{category.name}</p>
                </button>
              )
            })}
          </div>
        </div>

        {/* FAQs */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Preguntas Frecuentes
            </h2>
            <span className="text-sm text-gray-500">
              {filteredFAQs.length} {filteredFAQs.length === 1 ? 'resultado' : 'resultados'}
            </span>
          </div>

          {filteredFAQs.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No se encontraron resultados</p>
              <p className="text-sm text-gray-500">
                Intenta con otros términos de búsqueda o{' '}
                <Link to="/contact" className="text-primary-600 hover:text-primary-700">
                  contáctanos directamente
                </Link>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm sm:text-base font-medium text-gray-900 pr-4 flex-1">
                      {faq.question}
                    </span>
                    {expandedFAQ === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {expandedFAQ === faq.id && (
                    <div className="px-4 sm:px-6 pb-4">
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-sm sm:text-base text-gray-700 leading-relaxed mt-4 whitespace-pre-line">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg border border-primary-200 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                ¿No encontraste lo que buscabas?
              </h3>
              <p className="text-sm sm:text-base text-gray-700">
                Nuestro equipo está aquí para ayudarte. Contáctanos y te responderemos lo antes posible.
              </p>
            </div>
            <Link
              to="/contact"
              className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors whitespace-nowrap min-h-[44px] flex items-center justify-center"
            >
              Contáctanos
            </Link>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/terms"
            className="bg-white rounded-lg border border-gray-200 p-4 hover:border-primary-300 hover:shadow-md transition-all flex items-center gap-3"
          >
            <FileText className="w-6 h-6 text-primary-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Términos y Condiciones</h3>
              <p className="text-sm text-gray-500">Lee nuestros términos de servicio</p>
            </div>
          </Link>
          <Link
            to="/privacy"
            className="bg-white rounded-lg border border-gray-200 p-4 hover:border-primary-300 hover:shadow-md transition-all flex items-center gap-3"
          >
            <Shield className="w-6 h-6 text-primary-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Política de Privacidad</h3>
              <p className="text-sm text-gray-500">Cómo protegemos tus datos</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default HelpCenter

